// Import React hooks
import React,
{
    // Import memo hook
    useMemo,

    // Import state hook
    useState,
} from "react";

// Import utilities
import { formatDisplayDateTime } from "global/utilities/ConvertDate";

// Import css
import "./OrderProgressTimeline.css";
import { ArrowDownCollapseIcon, ArrowUpCollapseIcon } from "global/strich_icons/StrichIcons";



// Export the order progress timeline component
const OrderProgressTimeline = ({

    // The form values object
    formValues,

    // Shared form change handler
    handleChange,

}) => {

    // Track whether the progress panel is open
    const [isOpen, setIsOpen] = useState(false);

    // Track which date fields are currently editable
    const [editingFields, setEditingFields] = useState({});

    // Track temporary date and time edits so the timeline does not reorder until confirmed
    const [draftDateParts, setDraftDateParts] = useState({});



    // Store the main possible lifecycle steps
    const baseSteps = useMemo(() => {

        // Return the available steps
        return [
            {
                // Store the form field key
                key: "creationdate",

                // Store the display label
                label: "Created",

                // Store whether the field is editable
                editable: true,

                // Always show this step
                showWhenEmpty: true,
            },
            {
                // Store the form field key
                key: "paiddate",

                // Store the display label
                label: "Paid",

                // Store whether the field is editable
                editable: true,

                // Always show this step
                showWhenEmpty: true,
            },
            
            {
                // Store the form field key
                key: "produced",

                // Store the display label
                label: "Produced",

                // Store whether the field is editable
                editable: true,

                // Always show this step
                showWhenEmpty: true,
            },
            {
                // Store the form field key
                key: "shippeddate",

                // Store the display label
                label: "Shipped",

                // Store whether the field is editable
                editable: true,

                // Always show this step
                showWhenEmpty: true,
            },
            {
                // Store the form field key
                key: "cancelleddate",

                // Store the display label
                label: "Cancelled",

                // Store whether the field is editable
                editable: true,

                // Only show when a real date exists
                showWhenEmpty: false,
            },
            {
                // Store the form field key
                key: "refundeddate",

                // Store the display label
                label: "Refunded",

                // Store whether the field is editable
                editable: true,

                // Only show when a real date exists
                showWhenEmpty: false,
            },
        ];

    }, []);

    /* 
    {
        // Store the form field key
        key: "capturedate",

        // Store the display label
        label: "Captured",

        // Store whether the field is editable
        editable: false,

        // Always show this step
        showWhenEmpty: true,
    }, 
    */



    // Get the current local time in HH:mm:ss format
    const getCurrentTimeValue = () => {

        // Create a new date for the current local time
        const now = new Date();

        // Read the current hours padded to two digits
        const hours = String(now.getHours()).padStart(2, "0");

        // Read the current minutes padded to two digits
        const minutes = String(now.getMinutes()).padStart(2, "0");

        // Read the current seconds padded to two digits
        const seconds = String(now.getSeconds()).padStart(2, "0");

        // Return the formatted time
        return `${hours}:${minutes}:${seconds}`;

    };

    // Split a stored value into date and time input values - Taken out MVP
    /*
    const getEditableDateParts = (value) => {

        // Return empty date and current time when the value is not a real date
        if (!hasRealDate(value)) {

            // Return default parts
            return {
                date: "",
                time: getCurrentTimeValue(),
            };

        }

        // Normalise the stored value into a datetime-like string
        const normalisedValue = String(value).replace(" ", "T");

        // Read the date section
        const datePart = normalisedValue.slice(0, 10);

        // Read the time section safely including seconds
        const timePart = normalisedValue.slice(11, 19) || getCurrentTimeValue();

        // Return the split parts
        return {
            date: datePart,
            time: timePart,
        };

    };
    */

    // Build a single datetime value from date and time parts
    const buildDateTimeValue = (dateValue, timeValue) => {

        // Return an empty string when no date exists
        if (!dateValue) {

            // Return empty
            return "";

        }

        // Use the provided time or fall back to the current local time
        const safeTimeValue = timeValue || getCurrentTimeValue();

        // Return the combined datetime-local style value
        return `${dateValue}T${safeTimeValue}`;

    };



    // Check whether a value is the api "empty" date
    const isZeroDate = (value) => {

        // Stop when there is no value
        if (!value) {

            // Return true
            return true;

        }

        // Normalise the value into a string
        const normalisedValue = String(value).trim();

        // Return true when the value is one of the known empty date forms
        return (
            normalisedValue === "" ||
            normalisedValue === "0001-01-01 00:00:00" ||
            normalisedValue === "0001-01-01T00:00:00" ||
            normalisedValue === "0001-01-01"
        );

    };



    // Check whether a step has a real date value
    const hasRealDate = (value) => {

        // Return false for empty placeholder dates
        if (isZeroDate(value)) {

            // Return false
            return false;

        }

        // Create a date instance
        const parsedDate = new Date(value);

        // Return true only for valid dates
        return !Number.isNaN(parsedDate.getTime());

    };



    // Convert a value into a sortable timestamp
    const getDateTimestamp = (value) => {

        // Return null when the date is not real
        if (!hasRealDate(value)) {

            // Return null
            return null;

        }

        // Return the timestamp
        return new Date(value).getTime();

    };



    // Format a stored value into a readable display string
    const formatDisplayDate = (value) => {

        // Return a fallback when the value is not a real date
        if (!hasRealDate(value)) {

            // Return a fallback label
            return "";

        }

        // Format the date and time in local time
        const formatedDate = formatDisplayDateTime(value);

        return formatedDate;

    };


    // Get the most recent produced date from all order items
    const latestProducedDate = useMemo(() => {

        // Get safe order items array
        const safeOrderItems = Array.isArray(formValues?.orderitems)
            ? formValues.orderitems
            : [];

        // Find the latest produced date
        return safeOrderItems.reduce((latestProducedDateValue, orderItem) => {

            // Skip items without a produced date or a blank date
            if (!orderItem?.produced || orderItem.produced === "0001-01-01 00:00:00" ) {

                return latestProducedDateValue;

            }

            // Convert the current produced UTC date into a timestamp
            const currentTimestamp = new Date(
                orderItem.produced.replace(" ", "T") + "Z"
            ).getTime();

            // Skip invalid dates
            if (Number.isNaN(currentTimestamp)) {

                return latestProducedDateValue;

            }

            // If there is no latest date yet, use this one
            if (!latestProducedDateValue) {

                return orderItem.produced;

            }

            // Convert the latest produced UTC date into a timestamp
            const latestTimestamp = new Date(
                latestProducedDateValue.replace(" ", "T") + "Z"
            ).getTime();

            // Return the newest date string
            return currentTimestamp > latestTimestamp
                ? orderItem.produced
                : latestProducedDateValue;

        }, undefined);

    // Recalculate when order items change
    }, [formValues?.orderitems]);

    

    // Build production status data for all order items
    const productionItems = useMemo(() => {

        // Get safe order items array
        const safeOrderItems = Array.isArray(formValues?.orderitems)
            ? formValues.orderitems
            : [];

        // Return mapped production data
        return safeOrderItems.map((orderItem) => ({

            // Store item id
            id: orderItem.id,

            // Store title
            title: orderItem.title,

            // Store produced date
            produced: orderItem.produced,

            // Store produced status
            isProduced: Boolean(orderItem.produced && orderItem.produced !== "0001-01-01 00:00:00"),

        }));

    }, [formValues?.orderitems]);



    // Count produced items
    const producedItemCount = productionItems.filter(
        (item) => item.isProduced
    ).length;



    // Count all items
    const totalItemCount = productionItems.length;



    // Build the steps in actual saved date order
    const mappedSteps = useMemo(() => {

        // First enrich each step with its value and parsed timestamp
        const enrichedSteps = baseSteps.map((step) => {
          
            // Read the raw value from formValues
            const rawValue = step.key === "produced"
                ? latestProducedDate
                : formValues?.[step.key];

            // Convert to timestamp when valid
            const timestamp = getDateTimestamp(rawValue);

            // Return the enriched step
            return {
                // Keep the base step properties
                ...step,

                // Store the raw value
                value: rawValue,

                // Store the timestamp
                timestamp,

                // Store whether the step is complete
                isComplete: timestamp !== null,
            };

        });

        // Hide optional steps when they should not be shown yet
        const visibleSteps = enrichedSteps.filter((step) => {

            // Always show steps marked as showWhenEmpty (even if they have no date)
            if (step.showWhenEmpty) {

                // Keep this step
                return true;

            }

            // Only show optional steps when they have a real date
            return step.isComplete;

        });

        // Sort completed dates first in ascending order, then incomplete ones at the end
        const sortedSteps = [...visibleSteps].sort((firstStep, secondStep) => {

            // Put completed steps before incomplete steps
            if (firstStep.timestamp !== null && secondStep.timestamp === null) {

                // Return first step earlier
                return -1;

            }

            // Put incomplete steps after completed steps
            if (firstStep.timestamp === null && secondStep.timestamp !== null) {

                // Return second step earlier
                return 1;

            }

            // Keep two incomplete steps in their original relative order
            if (firstStep.timestamp === null && secondStep.timestamp === null) {

                // Keep current order
                return 0;

            }

            // Sort two completed steps by actual date
            return firstStep.timestamp - secondStep.timestamp;

        });

        // Find the index of the latest completed step in the sorted list
        const latestCompletedSortedIndex = sortedSteps.reduce((latestIndex, step, index) => {

            // Update the latest index when this step is complete
            return step.isComplete ? index : latestIndex;

        }, -1);

        // Return the fully prepared step list
        return sortedSteps.map((step, index) => ({

            // Keep the step properties
            ...step,

            // Mark the latest completed step as current
            isCurrent: step.isComplete && index === latestCompletedSortedIndex,

            // Mark incomplete steps as upcoming
            isUpcoming: !step.isComplete,

            // No skipped logic currently needed
            isSkipped: false,

        }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseSteps, formValues, latestProducedDate]);



    // Find the latest completed step from the sorted list
    const latestCompletedStep = useMemo(() => {

        // Get only completed steps
        const completedSteps = mappedSteps.filter((step) => step.isComplete);

        // Return null if none exist
        if (completedSteps.length === 0) {

            // Return null
            return null;

        }

        // Return the last completed step in date order
        return completedSteps[completedSteps.length - 1];

    }, [mappedSteps]);



    // Build the summary text shown in the closed header
    const summaryText = useMemo(() => {
        
        // Return a fallback when nothing has happened yet
        if (!latestCompletedStep) {

            // Return fallback text
            return "No progress recorded";

        }

        // Return the summary as JSX because formatDisplayDateTime returns JSX
        return (
            <>
                {latestCompletedStep.label} · {formatDisplayDate(latestCompletedStep.value)}
            </>
        );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestCompletedStep]);




    // Toggle the whole progress area open or closed
    const handleToggleOpen = () => {

        // Invert the open state
        setIsOpen((previousValue) => !previousValue);

    };

    // Enable edit mode for a field - Taken out MVP
    /*
    const enableFieldEdit = (fieldName) => {

        // Seed the draft date and time parts from the current saved form value
        setDraftDateParts((previousValue) => ({
            // Keep existing draft values
            ...previousValue,

            // Add the current field draft parts
            [fieldName]: getEditableDateParts(formValues?.[fieldName]),
        }));

        // Update the edit map
        setEditingFields((previousValue) => ({
            // Keep the existing edit states
            ...previousValue,

            // Enable the requested field
            [fieldName]: true,
        }));

    };
    */


    // Update a temporary draft date value while editing
    const handleDraftDateChange = (event) => {

        // Read the changed field name and value
        const { name, value } = event.target;

        // Save only the local draft date value so the timeline does not reorder yet
        setDraftDateParts((previousValue) => ({
            // Keep existing draft values
            ...previousValue,

            // Update this field's draft parts
            [name]: {
                // Keep any existing time value
                ...(previousValue?.[name] || {
                    time: getCurrentTimeValue(),
                }),

                // Save the new date value
                date: value,
            },
        }));

    };



    // Update a temporary draft time value while editing
    const handleDraftTimeChange = (event) => {

        // Read the changed field name and value
        const { name, value } = event.target;

        // Save only the local draft time value so the timeline does not reorder yet
        setDraftDateParts((previousValue) => ({
            // Keep existing draft values
            ...previousValue,

            // Update this field's draft parts
            [name]: {
                // Keep any existing date value
                ...(previousValue?.[name] || {
                    date: "",
                }),

                // Save the new time value
                time: value,
            },
        }));

    };



    // Disable edit mode for a field and save the draft value
    const disableFieldEdit = (fieldName) => {

        // Read the current draft parts
        const currentDraftParts = draftDateParts?.[fieldName] || {
            date: "",
            time: getCurrentTimeValue(),
        };

        // Build the final datetime value
        const nextValue = buildDateTimeValue(
            currentDraftParts.date,
            currentDraftParts.time
        );

        // Push the confirmed value into the parent form state
        handleChange({
            // Mimic a normal input event target
            target: {
                // Provide the field name
                name: fieldName,

                // Provide the combined datetime value
                value: nextValue,
            },
        });

        // Close edit mode
        setEditingFields((previousValue) => ({
            // Keep existing field edit states
            ...previousValue,

            // Disable this field
            [fieldName]: false,
        }));

        // Remove the draft field from local state
        setDraftDateParts((previousValue) => {

            // Copy the old draft map
            const nextDraftDateParts = { ...previousValue };

            // Remove this field from the copy
            delete nextDraftDateParts[fieldName];

            // Return the cleaned draft object
            return nextDraftDateParts;

        });

    };



    // Render the component
    return (

        // Outer wrapper
        <div className="order-progress">

            {/* Toggle button for the whole component */}
            <button
                // Set button type
                type="button"

                // Add component class
                className="order-progress__toggle"

                // Toggle the panel when clicked
                onClick={handleToggleOpen}

                // Accessibility state
                aria-expanded={isOpen}
            >

                {/* Left side summary block */}
                <span className="order-progress__toggle-content">

                    {/* Main heading 
                    <span className="order-progress__toggle-title">
                        View Order Progress
                    </span>
                    */}

                    {/* Current status summary */}
                    <span className="order-progress__toggle-summary">
                        {summaryText}
                    </span>

                </span>

                {/* Plus minus icon */}
                <span className="order-progress__toggle-icon">
                    {isOpen ? <ArrowUpCollapseIcon /> : <ArrowDownCollapseIcon />}
                </span>

            </button>



            {/* Expanded content */}
            {isOpen && (

                // Expanded panel
                <div className="order-progress__panel">

                    {/* Desktop and mobile responsive progress rail */}
                    <div className="order-progress__steps">

                        {/* Loop each ordered lifecycle step */}
                        {mappedSteps.map((step, index) => {

                            // Check whether this field is currently being edited
                            const isEditing = Boolean(editingFields?.[step.key]);

                            // Check whether the connector after this step should be shown
                            const showConnector = index < mappedSteps.length - 1;

                            // Build the state class name
                            const stateClassName = step.isCurrent
                                ? "order-progress__step--current"
                                : step.isComplete
                                    ? "order-progress__step--complete"
                                    : step.isUpcoming
                                        ? "order-progress__step--upcoming"
                                        : "order-progress__step--skipped";

                            // Render the step
                            return (

                                // Step item
                                <div
                                    // Use the field key as the react key
                                    key={step.key}

                                    // Apply the step class and visual state class
                                    className={`order-progress__step ${stateClassName}`}
                                >

                                    {/* Top progress marker row */}
                                    <div className="order-progress__step-top">

                                        {/* Step marker */}
                                        <div className="order-progress__marker">

                                            {/* Marker inner dot */}
                                            <span className="order-progress__marker-inner" />

                                        </div>

                                        {/* Connector after the marker */}
                                        {showConnector && (

                                            // Connector container
                                            <div className="order-progress__connector">

                                                {/* Connector line */}
                                                <span className="order-progress__connector-line" />

                                                {/* Connector arrow head */}
                                                <span className="order-progress__connector-arrow" />

                                            </div>

                                        )}

                                    </div>



                                    {/* Step card */}
                                    <div className="order-progress__card">

                                        {/* Label row */}
                                        <div className="order-progress__card-header">

                                            {/* Step label */}
                                            <span className="order-progress__label">
                                                {step.label}
                                            </span>

                                            {/* Edit button when editable and not already editing */}
                                            {/* Taken out MVP - users can't amend orders yet 
                                            {handleChange && step.editable && !isEditing && (
                                                <button
                                                    // Set button type
                                                    type="button"

                                                    // Add title text
                                                    title="Edit Date"

                                                    // Apply button class
                                                    className="order-progress__edit-button order-progress__edit-button--edit"

                                                    // Enable field editing
                                                    onClick={() => enableFieldEdit(step.key)}
                                                >
                                                    ✎
                                                </button>
                                            )}
                                            */}

                                            {/* Done button when in edit mode */}
                                            {handleChange && step.editable && isEditing && (
                                                <button
                                                    // Set button type
                                                    type="button"

                                                    // Add title text
                                                    title="Confirm"

                                                    // Apply button class
                                                    className="order-progress__edit-button"

                                                    // Disable field editing and save
                                                    onClick={() => disableFieldEdit(step.key)}
                                                >
                                                    ✔
                                                </button>
                                            )}

                                        </div>



                                        {/* Display mode date */}
                                        {!isEditing && (
                                            <div className="order-progress__date">

                                                {formatDisplayDate(step.value)}

                                                {/* Production breakdown */}
                                                {step.key === "produced" && productionItems.length > 0 && (

                                                    <div className="order-progress__production-breakdown">

                                                        <div className="order-progress__production-summary">

                                                            {producedItemCount} of {totalItemCount} item{totalItemCount !== 1 ? "s" : ""} produced

                                                        </div>

                                                        <div className="order-progress__production-list">

                                                            {productionItems.map((item) => (

                                                                <div
                                                                    key={item.id}
                                                                    className={`order-progress__production-item ${
                                                                        item.isProduced
                                                                            ? "order-progress__production-item--complete"
                                                                            : "order-progress__production-item--pending"
                                                                    }`}
                                                                >

                                                                    <span className="order-progress__production-icon">
                                                                        {item.isProduced ? "✓" : "○"}
                                                                    </span>

                                                                    <div className="order-progress__production-content">

                                                                        <div className="order-progress__production-title">
                                                                            {item.title}
                                                                        </div>

                                                                        <div className="order-progress__production-date">

                                                                            {item.isProduced
                                                                                ? formatDisplayDate(item.produced)
                                                                                : "Awaiting production"}

                                                                        </div>

                                                                    </div>

                                                                </div>

                                                            ))}

                                                        </div>

                                                    </div>

                                                )}

                                            </div>
                                        )}

                                        {/* Edit mode input */}
                                        {isEditing && (
                                            <div className="order-progress__input-row">

                                                {/* Date input */}
                                                <input
                                                    // Apply input class
                                                    className="order-progress__input order-progress__input--date"

                                                    // Bind the field name
                                                    name={step.key}

                                                    // Use date input
                                                    type="date"

                                                    // Use the local draft date value
                                                    value={draftDateParts?.[step.key]?.date ?? ""}

                                                    // Only update local draft date state while editing
                                                    onChange={handleDraftDateChange}
                                                />

                                                {/* Time input */}
                                                <input
                                                    className="order-progress__input order-progress__input--time"
                                                    name={step.key}
                                                    type="time"
                                                    step="1"
                                                    value={draftDateParts?.[step.key]?.time ?? getCurrentTimeValue()}
                                                    onChange={handleDraftTimeChange}
                                                />

                                            </div>
                                        )}

                                    </div>

                                </div>

                            );

                        })}

                    </div>


                </div>

            )}

        </div>

    );

};



// Export the component
export default OrderProgressTimeline;